import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Eye, Edit, Trash2, Users, Building2, UserCheck, AlertCircle, ChevronDown, Loader2, X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { AddDealPartyModal } from "./add-deal-party-modal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Deal Party type (matches crm_x_deal_parties schema)
interface DealParty {
  id: string;
  dealId: string;
  partyId: string;
  partyType: "account" | "contact";
  role: "borrower" | "guarantor" | "authorised_signatory" | "primary_contact" | "secondary_contact" | "associated_person";
  source: "manual" | "facility_propagated" | "association_propagated";
  sourceFacilityId?: string;
  status: "active" | "pending";
  associatedAccountId?: string;
  associationRole?: string;
  createdAt: string;

  // Resolved names (populated by API)
  partyName?: string;
  facilityName?: string; // Kept for backward compatibility (single facility)
  facilityNames?: string[]; // NEW: Array of all linked facilities
  associatedAccountName?: string;
}

interface SkippedParty {
  contactId: string;
  reason: string;
  existingRole?: string;
  contactName?: string;
}

interface DealPartiesPanelProps {
  dealId: string;
  clientGroupId?: string;
  transactionStage?: string; // NEW: For conditional button enabling
}

export function DealPartiesPanel({ dealId, clientGroupId, transactionStage }: DealPartiesPanelProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isAddPartyModalOpen, setIsAddPartyModalOpen] = useState(false);
  const [editingParty, setEditingParty] = useState<DealParty | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [partyToDelete, setPartyToDelete] = useState<DealParty | null>(null);

  // F008: Auto-propagation state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [clearAllAccountId, setClearAllAccountId] = useState<string | null>(null);
  const [clearAllAccountName, setClearAllAccountName] = useState<string | null>(null);
  const [skippedParties, setSkippedParties] = useState<SkippedParty[]>([]);
  const [showSkippedExpanded, setShowSkippedExpanded] = useState(false);

  // ISSUE-196 FIX: API returns grouped object {manual: [], facility_propagated: [], all: []}
  // We need to extract the .all property and ensure it's always an array
  const { data: partiesData, isLoading, refetch } = useQuery<{
    manual: DealParty[];
    facility_propagated: DealParty[];
    association_propagated: DealParty[];
    all: DealParty[];
  }>({
    queryKey: [`/api/crm/deals/${dealId}/parties`],
    enabled: !!dealId,
  });

  // Extract .all array, fallback to empty array if undefined/null
  const parties = Array.isArray(partiesData?.all) ? partiesData.all : [];

  const deleteMutation = useMutation({
    mutationFn: async (partyId: string) => {
      await apiRequest("DELETE", `/api/crm/deals/${dealId}/parties/${partyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      toast({
        title: "Party removed",
        description: "The party has been removed from this deal.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove party",
        variant: "destructive",
      });
    },
  });

  // F008: Auto-propagation mutation
  const autoPropagationMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const response = await apiRequest("POST", `/api/crm/deals/${dealId}/parties/auto-propagate`, {
        accountId,
        includeDirectors: true,
        includeShareholders: true,
        includeBeneficiaries: true,
        shareholderThreshold: 25,
      });
      return (await response.json()) as { propagatedParties: DealParty[]; count: number; skipped: SkippedParty[] };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      if (data.skipped && data.skipped.length > 0) {
        setSkippedParties(data.skipped);
      } else {
        setSkippedParties([]);
      }
      if (data.count === 0 && data.skipped.length > 0) {
        toast({
          title: "No new parties surfaced",
          description: `${data.skipped.length} contact(s) were already on this deal.`,
        });
      } else if (data.count > 0) {
        toast({
          title: "Associated persons surfaced",
          description: `${data.count} associated person(s) added to this deal.`,
        });
      } else {
        toast({
          title: "No associated persons found",
          description: "No directors, shareholders or beneficiaries found for this account.",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to surface associated persons",
        variant: "destructive",
      });
    },
  });

  // F008: Remove individual associated party (uses the existing DELETE /parties/:partyId endpoint)
  const removeAssociatedMutation = useMutation({
    mutationFn: async (partyId: string) => {
      await apiRequest("DELETE", `/api/crm/deals/${dealId}/parties/${partyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      toast({
        title: "Party removed",
        description: "The associated person has been removed from this deal.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove associated person",
        variant: "destructive",
      });
    },
  });

  // F008: Clear all auto-propagated parties for an account
  const clearAllAssociatedMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const response = await apiRequest("DELETE", `/api/crm/deals/${dealId}/parties/auto-propagated/${accountId}`);
      return (await response.json()) as { removed: number; accountId: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      setSkippedParties([]);
      toast({
        title: "Cleared",
        description: `${data.removed} associated person(s) removed from this deal.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to clear associated persons",
        variant: "destructive",
      });
    },
  });

  // F008: Inline promote for associated persons (using existing promote endpoint)
  const promoteAssociatedMutation = useMutation({
    mutationFn: async ({ partyId, targetRole }: { partyId: string; targetRole: string }) => {
      return await apiRequest("POST", `/api/crm/deals/${dealId}/parties/${partyId}/promote`, { targetRole });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      const roleLabel = variables.targetRole.split("_").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
      toast({
        title: "Promoted",
        description: `Person promoted to ${roleLabel}.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to promote person",
        variant: "destructive",
      });
    },
  });

  const handleDelete = (party: DealParty) => {
    // Only allow deletion of manually added parties
    if (party.source !== "manual") {
      toast({
        title: "Cannot remove",
        description: "This party was automatically added and cannot be manually removed.",
        variant: "destructive",
      });
      return;
    }

    setPartyToDelete(party);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (partyToDelete) {
      deleteMutation.mutate(partyToDelete.id);
      setIsDeleteDialogOpen(false);
      setPartyToDelete(null);
    }
  };

  const handleView = (party: DealParty) => {
    const route = party.partyType === "contact" ? `/contacts/${party.partyId}` : `/accounts/${party.partyId}`;
    navigate(route);
  };

  // F008: Auto-surface handler — find the first borrower account and propagate
  const handleAutoSurface = () => {
    // Find borrower accounts from the manual parties list
    const borrowerParties = manualParties.filter(
      (p) => p.role === "borrower" && p.partyType === "account"
    );
    if (borrowerParties.length === 0) {
      // Try contacts as borrowers if no account borrowers
      const borrowerAccounts = manualParties.filter((p) => p.role === "borrower");
      if (borrowerAccounts.length === 0) {
        toast({
          title: "No borrower found",
          description: "Add a borrower account to this deal before surfacing associated persons.",
          variant: "destructive",
        });
        return;
      }
    }

    // Use the first borrower account
    const borrower = borrowerParties[0] || manualParties.find((p) => p.role === "borrower");
    if (!borrower) return;

    autoPropagationMutation.mutate(borrower.partyId);
  };

  // F008: Clear all for a specific account
  const handleClearAll = (accountId: string, accountName?: string) => {
    setClearAllAccountId(accountId);
    setClearAllAccountName(accountName || null);
    setIsClearAllDialogOpen(true);
  };

  const handleClearAllConfirm = () => {
    if (clearAllAccountId) {
      clearAllAssociatedMutation.mutate(clearAllAccountId);
      setIsClearAllDialogOpen(false);
      setClearAllAccountId(null);
      setClearAllAccountName(null);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "borrower":
        return "bg-blue-500 text-white";
      case "guarantor":
        return "bg-purple-500 text-white";
      case "authorised_signatory":
        return "bg-green-500 text-white";
      case "primary_contact":
        return "bg-orange-500 text-white";
      case "secondary_contact":
        return "bg-amber-500 text-white";
      case "associated_person":
        return "bg-slate-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  // F008: Association role badge styling
  const getAssociationBadgeColor = (associationRole?: string) => {
    switch (associationRole) {
      case "director":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "shareholder_25":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "primary_beneficiary":
        return "bg-teal-100 text-teal-800 border-teal-200";
      default:
        return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  // F008: Association role display label
  const getAssociationLabel = (associationRole?: string) => {
    switch (associationRole) {
      case "director":
        return "Director";
      case "shareholder_25":
        return "Shareholder \u226525%";
      case "primary_beneficiary":
        return "Beneficiary";
      default:
        return associationRole?.replace(/_/g, " ") || "Associated";
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case "manual":
        return <UserCheck className="h-3 w-3" />;
      case "facility_propagated":
        return <Building2 className="h-3 w-3" />;
      case "association_propagated":
        return <Users className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case "manual":
        return "Manually Added";
      case "facility_propagated":
        return "From Facility";
      case "association_propagated":
        return "Auto-Surfaced";
      default:
        return source;
    }
  };

  const formatRoleDisplay = (role: string) => {
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // F011 FIX: Separate parties by SOURCE (not role)
  const manualParties = partiesData?.manual || [];
  const facilityParties = partiesData?.facility_propagated || [];

  // F008: Associated persons from association_propagated source
  const associatedPersons = partiesData?.association_propagated || parties.filter((p) =>
    p.source === "association_propagated"
  );

  // Group associated persons by account for clear-all actions
  const associatedByAccount = associatedPersons.reduce<Record<string, { accountName?: string; parties: DealParty[] }>>((acc, p) => {
    const accountId = p.associatedAccountId || "unknown";
    if (!acc[accountId]) {
      acc[accountId] = { accountName: p.associatedAccountName, parties: [] };
    }
    acc[accountId].parties.push(p);
    return acc;
  }, {});

  const uniqueAssociatedAccounts = Object.entries(associatedByAccount);

  return (
    <div className="space-y-4" data-testid="panel-deal-parties">
      {/* Deal-Level Parties Section (Manual) */}
      <div data-testid="section-manual-parties">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Deal-Level Parties</h3>
          <div className="flex items-center gap-2">
            {/* F008: Auto-Surface button */}
            <Button
              size="sm"
              variant="secondary"
              onClick={handleAutoSurface}
              disabled={autoPropagationMutation.isPending}
              data-testid="button-auto-surface"
            >
              {autoPropagationMutation.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Surfacing...
                </>
              ) : (
                <>
                  <Users className="h-3 w-3 mr-1" />
                  Auto-Surface Associated Persons
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddPartyModalOpen(true)}
              disabled={transactionStage !== 'scenario'}
              title={transactionStage !== 'scenario' ? 'Parties should be added at facility level when in deal or funded_loan stage' : 'Add a new party to this deal'}
              data-testid="button-add-party"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Party
            </Button>
          </div>
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="pt-3">
              <p className="text-sm text-muted-foreground text-center py-4">Loading parties...</p>
            </CardContent>
          </Card>
        ) : manualParties.length === 0 ? (
          <Card>
            <CardContent className="pt-3">
              <p className="text-sm text-muted-foreground text-center py-4">
                No parties added yet. Parties are linked through facilities or added manually.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="h-8 bg-muted/50">
                    <TableHead className="px-2 py-1 text-xs font-medium">Party</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Type</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Role</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Source</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Linked Facilities</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Status</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manualParties.map((party) => (
                    <TableRow key={party.id} className="h-8 hover:bg-muted/50" data-testid={`row-party-${party.id}`}>
                      <TableCell className="px-2 py-1">
                        <div className="flex items-center gap-1.5">
                          {party.partyType === "contact" ? (
                            <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          ) : (
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                          <span className="text-sm font-medium truncate">{party.partyName || "Unknown"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge variant="outline" className="text-xs capitalize">
                          {party.partyType}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge className={`text-xs ${getRoleBadgeColor(party.role)}`}>
                          {formatRoleDisplay(party.role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {getSourceIcon(party.source)}
                          {getSourceLabel(party.source)}
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <span className="text-xs text-muted-foreground">
                          {party.source === "manual"
                            ? "Manual"
                            : party.facilityNames && party.facilityNames.length > 0
                              ? party.facilityNames.join(", ")
                              : (party.facilityName || "-")}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge
                          variant={party.status === "active" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {party.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <div className="flex gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleView(party)}
                            title="View party details"
                            data-testid={`button-view-party-${party.id}`}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setEditingParty(party);
                              setIsAddPartyModalOpen(true);
                            }}
                            title="Edit party role/status"
                            data-testid={`button-edit-party-${party.id}`}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(party)}
                            title="Remove party"
                            data-testid={`button-delete-party-${party.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Facility Parties Section (Read-Only) */}
      {facilityParties.length > 0 && (
        <div data-testid="section-facility-parties">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Facility Parties</h3>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="h-8 bg-muted/50">
                    <TableHead className="px-2 py-1 text-xs font-medium">Party</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Type</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Role</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Source</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Linked Facilities</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Status</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium w-[60px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {facilityParties.map((party) => (
                    <TableRow key={party.id} className="h-8 hover:bg-muted/50" data-testid={`row-party-${party.id}`}>
                      <TableCell className="px-2 py-1">
                        <div className="flex items-center gap-1.5">
                          {party.partyType === "contact" ? (
                            <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          ) : (
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                          <span className="text-sm font-medium truncate">{party.partyName || "Unknown"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge variant="outline" className="text-xs capitalize">
                          {party.partyType}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge className={`text-xs ${getRoleBadgeColor(party.role)}`}>
                          {formatRoleDisplay(party.role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {getSourceIcon(party.source)}
                          {getSourceLabel(party.source)}
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <span className="text-xs text-muted-foreground">
                          {party.facilityNames && party.facilityNames.length > 0
                            ? party.facilityNames.join(", ")
                            : (party.facilityName || "-")}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge
                          variant={party.status === "active" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {party.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <div className="flex gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleView(party)}
                            title="View party details"
                            data-testid={`button-view-party-${party.id}`}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
            <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
            <span>
              These parties are automatically added from facilities. To edit or remove, modify the facility.
            </span>
          </p>
        </div>
      )}

      {/* F008: Skipped Parties Info Banner */}
      {skippedParties.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800"
          data-testid="banner-skipped-parties"
        >
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <button
              className="font-medium underline-offset-2 hover:underline focus:outline-none"
              onClick={() => setShowSkippedExpanded((prev) => !prev)}
              type="button"
            >
              {skippedParties.length} contact{skippedParties.length !== 1 ? "s" : ""} already on this deal{" "}
              {showSkippedExpanded ? "were" : "were"} skipped
              <ChevronDown
                className={`inline h-3 w-3 ml-1 transition-transform ${showSkippedExpanded ? "rotate-180" : ""}`}
              />
            </button>
            {showSkippedExpanded && (
              <ul className="mt-1 space-y-0.5 text-xs">
                {skippedParties.map((s) => (
                  <li key={s.contactId}>
                    {s.contactName || s.contactId}
                    {s.existingRole ? ` (${formatRoleDisplay(s.existingRole)})` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            className="text-blue-600 hover:text-blue-800 focus:outline-none"
            onClick={() => setSkippedParties([])}
            type="button"
            aria-label="Dismiss skipped parties banner"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* F008: Associated Persons Section (Auto-Surfaced) */}
      {associatedPersons.length > 0 && (
        <div data-testid="section-associated-persons">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Associated Persons</h3>
              <Badge variant="secondary" className="text-xs">
                {associatedPersons.length}
              </Badge>
            </div>
            {/* Clear all button — shows per account if multiple accounts */}
            {uniqueAssociatedAccounts.length === 1 && (
              <button
                className="text-xs text-destructive hover:underline focus:outline-none"
                onClick={() => {
                  const [accountId, { accountName }] = uniqueAssociatedAccounts[0];
                  handleClearAll(accountId, accountName);
                }}
                type="button"
                data-testid="button-clear-all-associated"
              >
                Clear Auto-Surfaced
              </button>
            )}
          </div>

          <Card className="border-muted">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="h-8 bg-muted/30">
                    <TableHead className="px-2 py-1 text-xs font-medium">Person</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Association</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium">Source Account</TableHead>
                    <TableHead className="px-2 py-1 text-xs font-medium w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {associatedPersons.map((party) => (
                    <TableRow
                      key={party.id}
                      className="h-8 bg-muted/50 hover:bg-muted/70"
                      data-testid={`row-associated-${party.id}`}
                    >
                      <TableCell className="px-2 py-1">
                        <div className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate italic">
                            {party.partyName || "Unknown"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge
                          variant="outline"
                          className={`text-xs ${getAssociationBadgeColor(party.associationRole)}`}
                          data-testid={`badge-association-${party.associationRole || "unknown"}`}
                        >
                          {getAssociationLabel(party.associationRole)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1 text-xs text-muted-foreground">
                        {party.associatedAccountName || "-"}
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <div className="flex gap-0.5 items-center">
                          {/* F008: Promote dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs px-2"
                                title="Promote to a deal role"
                                data-testid="dropdown-promote-trigger"
                              >
                                Promote
                                <ChevronDown className="h-3 w-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44" data-testid="dropdown-promote-menu">
                              <DropdownMenuLabel className="text-xs">Select Role</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-xs"
                                onSelect={() => promoteAssociatedMutation.mutate({ partyId: party.id, targetRole: "borrower" })}
                                data-testid="dropdown-item-promote-borrower"
                              >
                                Borrower
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onSelect={() => promoteAssociatedMutation.mutate({ partyId: party.id, targetRole: "guarantor" })}
                                data-testid="dropdown-item-promote-guarantor"
                              >
                                Guarantor
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onSelect={() => promoteAssociatedMutation.mutate({ partyId: party.id, targetRole: "authorised_signatory" })}
                                data-testid="dropdown-item-promote-signatory"
                              >
                                Authorised Signatory
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onSelect={() => promoteAssociatedMutation.mutate({ partyId: party.id, targetRole: "primary_contact" })}
                                data-testid="dropdown-item-promote-primary-contact"
                              >
                                Primary Contact
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-xs"
                                onSelect={() => promoteAssociatedMutation.mutate({ partyId: party.id, targetRole: "secondary_contact" })}
                                data-testid="dropdown-item-promote-secondary-contact"
                              >
                                Secondary Contact
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {/* F008: Remove individual */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => removeAssociatedMutation.mutate(party.id)}
                            title="Remove associated person"
                            data-testid={`button-remove-associated-${party.id}`}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Per-account clear-all links when multiple accounts */}
          {uniqueAssociatedAccounts.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {uniqueAssociatedAccounts.map(([accountId, { accountName, parties: acctParties }]) => (
                <button
                  key={accountId}
                  className="text-xs text-destructive hover:underline focus:outline-none"
                  onClick={() => handleClearAll(accountId, accountName)}
                  type="button"
                  data-testid="button-clear-all-associated"
                >
                  Clear {accountName || "account"} ({acctParties.length})
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Party Modal */}
      <AddDealPartyModal
        open={isAddPartyModalOpen}
        onOpenChange={(open) => {
          setIsAddPartyModalOpen(open);
          if (!open) setEditingParty(null);
        }}
        dealId={dealId}
        clientGroupId={clientGroupId}
        editingParty={editingParty}
        onPartyAdded={() => refetch()}
      />

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="dialog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Party from Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{partyToDelete?.partyName}</strong> from this deal?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-delete-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* F008: Clear All Auto-Surfaced Confirmation Dialog */}
      <AlertDialog open={isClearAllDialogOpen} onOpenChange={setIsClearAllDialogOpen}>
        <AlertDialogContent data-testid="dialog-clear-auto-surfaced">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Auto-Surfaced Persons</AlertDialogTitle>
            <AlertDialogDescription>
              Remove all auto-surfaced associated persons from{" "}
              <strong>{clearAllAccountName || "this account"}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAllConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-clear-confirm"
            >
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
